// src/features/sales/components/SalesMetricsDashboard.tsx
import React, { useEffect, useState, useMemo } from 'react'
import { RangeStrings } from '@shared/components/ui/DateRangePicker'
import { format, startOfDay, endOfDay } from 'date-fns'

import {
  fetchProductAttributesMap,
  fetchProductsApi,
} from '../../stock/api/productApi'
import { fetchAttributesApi } from '../../stock/api/attributeApi'
import {
  fetchProductReportApi,
  ProductReportItem,
} from '../api/SalesPurchaseApi'

import LineGraph from '@shared/components/ui/LineGraph'

type AttrProductReports = Record<
  number,
  Record<number, ProductReportItem[]>
>

export interface SalesMetricsDashboardProps {
  dateRange?: RangeStrings
}

export default function SalesMetricsDashboard({
  dateRange,
}: SalesMetricsDashboardProps) {
  const [products, setProducts]         = useState<Record<number,string>>({})
  const [attributes, setAttributes]     = useState<Record<number,string>>({})
  const [attrProductReports, setAttrProductReports] =
    useState<AttrProductReports>({})
  const [perProductReports, setPerProductReports] =
    useState<Record<number, ProductReportItem[]>>({})

  // ─── default to today if no dateRange prop ────────────────────────────────
  const defaultRange = useMemo<RangeStrings>(() => {
    const now = new Date()
    const iso = (d: Date) => format(d, "yyyy-MM-dd'T'HH:mm:ss")
    return { from: iso(startOfDay(now)), to: iso(endOfDay(now)) }
  }, [])
  const effectiveRange = dateRange ?? defaultRange

  // ─── Load lookups ─────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadLookups() {
      const [pl, al] = await Promise.all([
        fetchProductsApi(),
        fetchAttributesApi(),
      ])
      setProducts(Object.fromEntries(pl.map(p => [p.id_product, p.product_name])))
      setAttributes(Object.fromEntries(al.map(a => [a.id_attribute, a.attribute_name])))
    }
    loadLookups()
  }, [])

  // ─── Fetch metrics ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    async function loadMetrics() {
      const prodAttrMap = await fetchProductAttributesMap()
      const fetches = Object.keys(prodAttrMap).map(async pidStr => {
        const pid = Number(pidStr)
        const rpt = await fetchProductReportApi(
          pid,
          effectiveRange.from,
          effectiveRange.to,
          { kind: 'Sum', unit: 'Hour' }
        )
        return [pid, rpt] as const
      })
      const perProduct = new Map<number, ProductReportItem[]>(
        await Promise.all(fetches)
      )
      if (cancelled) return

      setPerProductReports(Object.fromEntries(perProduct.entries()))

      const attrRpt: AttrProductReports = {}
      for (const [pid, attrs] of Object.entries(prodAttrMap)) {
        const id = Number(pid)
        const rpt = perProduct.get(id) ?? []
        for (const attrId of attrs) {
          attrRpt[attrId] ??= {}
          attrRpt[attrId][id] = rpt
        }
      }
      if (!cancelled) setAttrProductReports(attrRpt)
    }
    loadMetrics()
    return () => { cancelled = true }
  }, [effectiveRange])

  // ─── Overall profit across all products ─────────────────────────────────
  const overallProfitData = useMemo(() => {
    const timestamps = new Set<string>()
    Object.values(perProductReports).forEach(rpt =>
      rpt.forEach(r => timestamps.add(r.timestamp))
    )
    return Array.from(timestamps)
      .sort()
      .map(ts => ({
        timestamp: ts,
        profit: Object.values(perProductReports).reduce(
          (sum, rpt) => sum + (rpt.find(r => r.timestamp === ts)?.profit || 0),
          0
        ),
      }))
  }, [perProductReports])

  // ─── Derive overview-by-attribute data ────────────────────────────────────
  const [attrQtyData, attrProfitData, attrKeys, attrLabels] = useMemo(() => {
    const allT = new Set<string>()
    for (const prodMap of Object.values(attrProductReports)) {
      for (const rpt of Object.values(prodMap)) rpt.forEach(r => allT.add(r.timestamp))
    }
    const timestamps = Array.from(allT).sort()

    const keys   = Object.keys(attrProductReports)
    const labels = keys.map(k => attributes[Number(k)] ?? `Attr ${k}`)

    const build = (field: 'qty' | 'profit') =>
      timestamps.map(ts => {
        const obj: Record<string, any> = { timestamp: ts }
        for (const k of keys) {
          const prodMap = attrProductReports[Number(k)]
          const sum = Object.values(prodMap).reduce((acc, rpt) => {
            const r = rpt.find(r => r.timestamp === ts)
            return acc + (r ? r[field] : 0)
          }, 0)
          obj[k] = sum
        }
        return obj
      })

    return [build('qty'), build('profit'), keys, labels] as const
  }, [attrProductReports, attributes])

  // ─── Loading state ────────────────────────────────────────────────────────
  if (!overallProfitData.length || !attrKeys.length) {
    return <p className="text-sm text-neutral-400">Loading metrics…</p>
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-12">

      {/* Overall profit (sum of all products) */}
      <LineGraph<{ timestamp: string; profit: number }>
        data={overallProfitData}
        xKey="timestamp"
        yKeys={['profit']}
        labels={['Total Profit (All Products)']}
        showGrid
        showLegend
        height={200}
      />

      {/* Overview: one line per attribute */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LineGraph<Record<string, any>>
          data={attrQtyData}
          xKey="timestamp"
          yKeys={attrKeys}
          labels={attrLabels}
          showGrid
          showLegend
          height={240}
        />
        <LineGraph<Record<string, any>>
          data={attrProfitData}
          xKey="timestamp"
          yKeys={attrKeys}
          labels={attrLabels}
          showGrid
          showLegend
          height={240}
        />
      </section>

      {/* By Attribute: breakdown per product */}
      {Object.entries(attrProductReports).map(([attrIdStr, prodMap]) => {
        const attrId = Number(attrIdStr)
        const title  = attributes[attrId] ?? `Attribute ${attrId}`
        const pids   = Object.keys(prodMap)
        const labels = pids.map(pid => products[Number(pid)] || `Prod ${pid}`)

        return (
          <section key={attrId} className="space-y-4">
            <h2 className="text-lg font-medium text-neutral-100">{title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <LineGraph<Record<string, any>>
                data={buildAttributeSeries(prodMap, 'qty')}
                xKey="timestamp"
                yKeys={pids}
                labels={labels}
                showGrid
                showLegend
                height={220}
              />
              <LineGraph<Record<string, any>>
                data={buildAttributeSeries(prodMap, 'profit')}
                xKey="timestamp"
                yKeys={pids}
                labels={labels}
                showGrid
                showLegend
                height={220}
              />
            </div>
          </section>
        )
      })}
    </div>
  )
}

// helper to build per-product series
function buildAttributeSeries(
  productMap: Record<number, ProductReportItem[]>,
  field: 'qty' | 'profit'
) {
  const allT = new Set<string>()
  for (const rpt of Object.values(productMap)) rpt.forEach(r => allT.add(r.timestamp))
  const timestamps = Array.from(allT).sort()

  const pids = Object.keys(productMap)
  return timestamps.map(ts => {
    const obj: Record<string, any> = { timestamp: ts }
    for (const pidStr of pids) {
      const rpt = productMap[Number(pidStr)]
      const r   = rpt.find(r => r.timestamp === ts)
      obj[pidStr] = r ? r[field] : 0
    }
    return obj
  })
}
